"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'

type AdapterRow = {
  id: string
  adapterType: string
  name: string
  pollingIntervalSeconds: number
  isActive: boolean
  lastPollAt: string | null
  lastError: string | null
  createdAt: string
}

type AdaptersResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

function mapAdapterRow(item: Record<string, unknown>): AdapterRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    adapterType: typeof item.adapter_type === 'string' ? item.adapter_type : '',
    name: typeof item.name === 'string' ? item.name : '',
    pollingIntervalSeconds: typeof item.polling_interval_seconds === 'number' ? item.polling_interval_seconds : 60,
    isActive: typeof item.is_active === 'boolean' ? item.is_active : true,
    lastPollAt: typeof item.last_poll_at === 'string' ? item.last_poll_at : null,
    lastError: typeof item.last_error === 'string' ? item.last_error : null,
    createdAt: typeof item.created_at === 'string' ? item.created_at : '',
  }
}

const ADAPTER_TYPE_LABELS: Record<string, string> = {
  GBOX: 'G-Box',
  TRIMBLE: 'Trimble',
  MQTT_GENERIC: 'MQTT Generic',
  WEBHOOK_GENERIC: 'Webhook Generic',
  MANUAL: 'Manual',
}

export default function RailTrackingPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'rail-tracking-list',
  })
  const [rows, setRows] = React.useState<AdapterRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()
  const router = useRouter()

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'adapterType',
      label: t('rail_tracking.adapters.filters.adapterType', 'Adapter Type'),
      type: 'select' as const,
      options: Object.entries(ADAPTER_TYPE_LABELS).map(([value, label]) => ({ value, label })),
    },
  ], [t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const adapterType = filterValues.adapterType
    if (typeof adapterType === 'string' && adapterType.trim()) params.set('adapterType', adapterType)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: AdaptersResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<AdaptersResponse>(
          `/api/rail_tracking/adapters?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('rail_tracking.adapters.error.load', 'Failed to load GPS adapters')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapAdapterRow).filter((row): row is AdapterRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('rail_tracking.adapters.error.load', 'Failed to load GPS adapters')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  const handleDelete = React.useCallback(async (adapter: AdapterRow) => {
    if (!adapter.id) return
    const confirmed = await confirm({
      title: t('rail_tracking.adapters.deleteConfirm', 'Delete adapter "{{name}}"?', { name: adapter.name }),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/rail_tracking/adapters?id=${encodeURIComponent(adapter.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('rail_tracking.adapters.deleteError', 'Failed to delete adapter') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((row) => row.id !== adapter.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('rail_tracking.adapters.deleteSuccess', 'GPS adapter deleted'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('rail_tracking.adapters.deleteError', 'Failed to delete adapter')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const handleFiltersApply = React.useCallback((values: FilterValues) => {
    setFilterValues(values)
    setPage(1)
  }, [])

  const handleFiltersClear = React.useCallback(() => {
    setFilterValues({})
    setPage(1)
  }, [])

  const columns = React.useMemo<ColumnDef<AdapterRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'name',
        header: t('rail_tracking.adapters.columns.name', 'Name'),
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        accessorKey: 'adapterType',
        header: t('rail_tracking.adapters.columns.adapterType', 'Type'),
        cell: ({ row }) => ADAPTER_TYPE_LABELS[row.original.adapterType] ?? row.original.adapterType,
      },
      {
        accessorKey: 'isActive',
        header: t('rail_tracking.adapters.columns.isActive', 'Status'),
        cell: ({ row }) => (
          <span className={row.original.isActive ? 'text-green-600' : 'text-muted-foreground'}>
            {row.original.isActive
              ? t('rail_tracking.adapters.status.active', 'Active')
              : t('rail_tracking.adapters.status.inactive', 'Inactive')}
          </span>
        ),
      },
      {
        accessorKey: 'pollingIntervalSeconds',
        header: t('rail_tracking.adapters.columns.pollingInterval', 'Interval'),
        cell: ({ row }) => `${row.original.pollingIntervalSeconds}s`,
      },
      {
        accessorKey: 'lastPollAt',
        header: t('rail_tracking.adapters.columns.lastPoll', 'Last Poll'),
        cell: ({ row }) => row.original.lastPollAt
          ? new Date(row.original.lastPollAt).toLocaleString()
          : noValue,
      },
      {
        accessorKey: 'lastError',
        header: t('rail_tracking.adapters.columns.lastError', 'Last Error'),
        cell: ({ row }) => row.original.lastError
          ? <span className="text-red-600 text-xs">{row.original.lastError}</span>
          : noValue,
      },
    ]
  }, [t])

  return (
    <Page>
      <PageBody>
        <DataTable<AdapterRow>
          title={t('rail_tracking.adapters.title', 'GPS Adapters')}
          refreshButton={{
            label: t('rail_tracking.adapters.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); handleRefresh() },
          }}
          actions={(
            <div className="flex gap-2">
              <Button asChild variant="outline">
                <Link href="/backend/rail-tracking/positions">
                  {t('rail_tracking.adapters.actions.positions', 'Position History')}
                </Link>
              </Button>
              <Button asChild>
                <Link href="/backend/rail-tracking/create">
                  {t('rail_tracking.adapters.actions.create', 'New Adapter')}
                </Link>
              </Button>
            </div>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('rail_tracking.adapters.searchPlaceholder', 'Search adapters...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={handleFiltersApply}
          onFiltersClear={handleFiltersClear}
          perspective={{ tableId: 'rail_tracking.adapters.list' }}
          onRowClick={(row) => router.push(`/backend/rail-tracking/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('rail_tracking.adapters.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
